import { Check } from '@mui/icons-material'
import { translate } from 'locales'
import React, { useMemo, useState } from 'react'
import { IBankAccountData } from '../types'
import {
  formatCnpj,
  formatCpfOrCnpj,
  formatDocument,
  formatPhone,
  isValidCNPJ,
  isValidCPF,
  isValidCpfOrCnpj,
  isValidEmail,
  isValidNumericWithCheckDigit,
  isValidPhone,
  sanitizeNumericWithCheckDigit,
} from '../utils'
import * as S from './styles'

interface BankDataFormProps {
  account: IBankAccountData
  saving?: boolean
  onCancel: () => void
  onSave: (updated: IBankAccountData) => void | Promise<void>
}

const ACCOUNT_TYPE_OPTIONS = [
  { value: 'checking', labelKey: 'financial.bankData.accountTypeChecking' },
  { value: 'savings', labelKey: 'financial.bankData.accountTypeSavings' },
]

const PIX_KEY_TYPE_OPTIONS = [
  { value: 'email', labelKey: 'financial.bankData.pixKeyTypeEmail' },
  { value: 'cpf_cnpj', labelKey: 'financial.bankData.pixKeyTypeCpfCnpj' },
  { value: 'phone', labelKey: 'financial.bankData.pixKeyTypePhone' },
  { value: 'random', labelKey: 'financial.bankData.pixKeyTypeRandom' },
]

const REQUIRED_FIELDS: (keyof IBankAccountData)[] = [
  'holderName',
  'cpf',
  'bankCode',
  'bankName',
  'accountType',
  'agency',
  'accountNumber',
]

const PIX_KEY_VALIDATORS: Record<string, (value: string) => boolean> = {
  email: isValidEmail,
  cpf_cnpj: isValidCpfOrCnpj,
  phone: isValidPhone,
}

const PIX_KEY_ERROR_KEYS: Record<string, string> = {
  email: 'financial.bankData.errors.invalidEmail',
  cpf_cnpj: 'financial.bankData.errors.invalidPixCpfCnpj',
  phone: 'financial.bankData.errors.invalidPhone',
}

type FieldErrors = Partial<Record<keyof IBankAccountData, string>>

const getFieldErrors = (data: IBankAccountData): FieldErrors => {
  const errors: FieldErrors = {}

  REQUIRED_FIELDS.forEach((field) => {
    if (!data[field].trim()) {
      errors[field] = translate('financial.bankData.errors.required')
    }
  })

  if (!errors.cpf && !isValidCPF(data.cpf)) {
    errors.cpf = translate('financial.bankData.errors.invalidCpf')
  }

  if (data.cnpj.trim() && !isValidCNPJ(data.cnpj)) {
    errors.cnpj = translate('financial.bankData.errors.invalidCnpj')
  }

  if (!errors.agency && !isValidNumericWithCheckDigit(data.agency)) {
    errors.agency = translate('financial.bankData.errors.invalidNumeric')
  }

  if (
    !errors.accountNumber &&
    !isValidNumericWithCheckDigit(data.accountNumber)
  ) {
    errors.accountNumber = translate('financial.bankData.errors.invalidNumeric')
  }

  const pixKeyFilled = Boolean(data.pixKey.trim())
  const pixKeyTypeFilled = Boolean(data.pixKeyType)

  if (pixKeyFilled && !pixKeyTypeFilled) {
    errors.pixKeyType = translate(
      'financial.bankData.errors.pixKeyTypeRequired'
    )
  } else if (pixKeyTypeFilled && !pixKeyFilled) {
    errors.pixKey = translate('financial.bankData.errors.pixKeyRequired')
  } else if (pixKeyFilled && pixKeyTypeFilled) {
    const validatePixKey = PIX_KEY_VALIDATORS[data.pixKeyType]
    if (validatePixKey && !validatePixKey(data.pixKey)) {
      errors.pixKey = translate(PIX_KEY_ERROR_KEYS[data.pixKeyType])
    }
  }

  return errors
}

const BankDataForm = ({
  account,
  saving,
  onCancel,
  onSave,
}: BankDataFormProps) => {
  const [form, setForm] = useState<IBankAccountData>(account)
  const [touched, setTouched] = useState<Partial<Record<string, boolean>>>(
    () => {
      const initialErrors = getFieldErrors(account)
      const initialTouched: Partial<Record<string, boolean>> = {}

      Object.keys(initialErrors).forEach((field) => {
        const key = field as keyof IBankAccountData
        if (account[key].trim()) {
          initialTouched[key] = true
        }
      })

      return initialTouched
    }
  )
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)

  const errors = useMemo(() => getFieldErrors(form), [form])
  const canSubmit = Object.keys(errors).length === 0

  const shouldShowError = (field: keyof IBankAccountData): boolean =>
    Boolean(errors[field]) && (touched[field] || attemptedSubmit)

  const handleBlur = (field: keyof IBankAccountData) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }))
  }

  const handleChange =
    (
      field: keyof IBankAccountData
    ): React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement> =>
    (e) => {
      const { value } = e.target
      setForm((prev) => ({ ...prev, [field]: value }))
    }

  const handleDocumentChange =
    (field: 'cpf' | 'cnpj'): React.ChangeEventHandler<HTMLInputElement> =>
    (e) => {
      const formatted =
        field === 'cnpj'
          ? formatCnpj(e.target.value)
          : formatDocument(e.target.value)
      setForm((prev) => ({ ...prev, [field]: formatted }))
    }

  const handleNumericChange =
    (
      field: 'agency' | 'accountNumber'
    ): React.ChangeEventHandler<HTMLInputElement> =>
    (e) => {
      const sanitized = sanitizeNumericWithCheckDigit(e.target.value)
      setForm((prev) => ({ ...prev, [field]: sanitized }))
    }

  const handlePixKeyTypeChange: React.ChangeEventHandler<HTMLSelectElement> = (
    e
  ) => {
    const { value } = e.target
    setForm((prev) => ({ ...prev, pixKeyType: value, pixKey: '' }))
  }

  const handlePixKeyChange: React.ChangeEventHandler<HTMLInputElement> = (
    e
  ) => {
    const { value } = e.target
    setForm((prev) => {
      if (prev.pixKeyType === 'cpf_cnpj') {
        return { ...prev, pixKey: formatCpfOrCnpj(value) }
      }

      if (prev.pixKeyType === 'phone') {
        return { ...prev, pixKey: formatPhone(value) }
      }

      return { ...prev, pixKey: value }
    })
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!canSubmit) {
      setAttemptedSubmit(true)
      return
    }

    onSave(form)
  }

  return (
    <form onSubmit={handleSubmit}>
      <S.FormGrid>
        <S.FormField fullWidth>
          <label htmlFor="holderName">
            {translate('financial.bankData.accountHolder')}
            <S.RequiredMark>*</S.RequiredMark>
          </label>
          <input
            id="holderName"
            name="holderName"
            value={form.holderName}
            onChange={handleChange('holderName')}
            onBlur={handleBlur('holderName')}
          />
          {shouldShowError('holderName') && (
            <S.FieldError>{errors.holderName}</S.FieldError>
          )}
        </S.FormField>

        <S.FormField fullWidth>
          <label htmlFor="companyName">
            {translate('financial.bankData.companyName')}
          </label>
          <input
            id="companyName"
            name="companyName"
            value={form.companyName}
            onChange={handleChange('companyName')}
          />
        </S.FormField>

        <S.FormField>
          <label htmlFor="cpf">
            {translate('financial.bankData.cpf')}
            <S.RequiredMark>*</S.RequiredMark>
          </label>
          <input
            id="cpf"
            name="cpf"
            value={form.cpf}
            onChange={handleDocumentChange('cpf')}
            onBlur={handleBlur('cpf')}
          />
          {shouldShowError('cpf') && <S.FieldError>{errors.cpf}</S.FieldError>}
        </S.FormField>

        <S.FormField>
          <label htmlFor="cnpj">{translate('financial.bankData.cnpj')}</label>
          <input
            id="cnpj"
            name="cnpj"
            value={form.cnpj}
            onChange={handleDocumentChange('cnpj')}
            onBlur={handleBlur('cnpj')}
          />
          {shouldShowError('cnpj') && (
            <S.FieldError>{errors.cnpj}</S.FieldError>
          )}
        </S.FormField>

        <S.FormField>
          <label htmlFor="bankCode">
            {translate('financial.bankData.bankCode')}
            <S.RequiredMark>*</S.RequiredMark>
          </label>
          <input
            id="bankCode"
            name="bankCode"
            value={form.bankCode}
            onChange={handleChange('bankCode')}
            onBlur={handleBlur('bankCode')}
          />
          {shouldShowError('bankCode') && (
            <S.FieldError>{errors.bankCode}</S.FieldError>
          )}
        </S.FormField>

        <S.FormField>
          <label htmlFor="bankName">
            {translate('financial.bankData.bankName')}
            <S.RequiredMark>*</S.RequiredMark>
          </label>
          <input
            id="bankName"
            name="bankName"
            value={form.bankName}
            onChange={handleChange('bankName')}
            onBlur={handleBlur('bankName')}
          />
          {shouldShowError('bankName') && (
            <S.FieldError>{errors.bankName}</S.FieldError>
          )}
        </S.FormField>

        <S.FormField>
          <label htmlFor="accountType">
            {translate('financial.bankData.accountType')}
            <S.RequiredMark>*</S.RequiredMark>
          </label>
          <select
            id="accountType"
            name="accountType"
            value={form.accountType}
            onChange={handleChange('accountType')}
            onBlur={handleBlur('accountType')}
          >
            <option value="">
              {translate('financial.bankData.selectPlaceholder')}
            </option>
            {ACCOUNT_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {translate(option.labelKey)}
              </option>
            ))}
          </select>
          {shouldShowError('accountType') && (
            <S.FieldError>{errors.accountType}</S.FieldError>
          )}
        </S.FormField>

        <S.FormField>
          <label htmlFor="agency">
            {translate('financial.bankData.agency')}
            <S.RequiredMark>*</S.RequiredMark>
          </label>
          <input
            id="agency"
            name="agency"
            value={form.agency}
            onChange={handleNumericChange('agency')}
            onBlur={handleBlur('agency')}
          />
          {shouldShowError('agency') && (
            <S.FieldError>{errors.agency}</S.FieldError>
          )}
        </S.FormField>

        <S.FormField>
          <label htmlFor="accountNumber">
            {translate('financial.bankData.account')}
            <S.RequiredMark>*</S.RequiredMark>
          </label>
          <input
            id="accountNumber"
            name="accountNumber"
            value={form.accountNumber}
            onChange={handleNumericChange('accountNumber')}
            onBlur={handleBlur('accountNumber')}
          />
          {shouldShowError('accountNumber') && (
            <S.FieldError>{errors.accountNumber}</S.FieldError>
          )}
        </S.FormField>

        <S.FormField>
          <label htmlFor="pixKeyType">
            {translate('financial.bankData.pixKeyType')}
          </label>
          <select
            id="pixKeyType"
            name="pixKeyType"
            value={form.pixKeyType}
            onChange={handlePixKeyTypeChange}
            onBlur={handleBlur('pixKeyType')}
          >
            <option value="">
              {translate('financial.bankData.selectPlaceholder')}
            </option>
            {PIX_KEY_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {translate(option.labelKey)}
              </option>
            ))}
          </select>
          {shouldShowError('pixKeyType') && (
            <S.FieldError>{errors.pixKeyType}</S.FieldError>
          )}
        </S.FormField>

        <S.FormField>
          <label htmlFor="pixKey">
            {translate('financial.bankData.pixKey')}
          </label>
          <input
            id="pixKey"
            name="pixKey"
            value={form.pixKey}
            onChange={handlePixKeyChange}
            onBlur={handleBlur('pixKey')}
          />
          {shouldShowError('pixKey') && (
            <S.FieldError>{errors.pixKey}</S.FieldError>
          )}
        </S.FormField>
      </S.FormGrid>

      <S.FormActions>
        <S.CancelButton type="button" onClick={onCancel} disabled={saving}>
          {translate('financial.bankData.cancel')}
        </S.CancelButton>

        <S.SaveButton type="submit" disabled={saving || !canSubmit}>
          <Check />
          {translate('financial.bankData.save')}
        </S.SaveButton>
      </S.FormActions>
    </form>
  )
}

export default BankDataForm
